import moment from 'moment';
import IOrderItem from '../../models/IOrderItem';
import { transformArrayFromModifiers } from '../modifiers';
import { roundToNext30Min } from '../../services/IsClosed';

// Helper function to replace commas
const replaceComma = (str: string) => {
  return str?.replace(/,/g, '') || str;
};

interface PayLaterHandlerParams {
  // State values
  state: {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    email: string;
    notes: string;
    orderType: string;
    undeliverablePostalCodes: string[];
    undeliverableDistances: string[];
    undeliverableTravelTime: string[];
    postalCode: string;
    selectedAddressId: string;
    savedAddresses: any[];
    saveAddress: boolean;
    addressName: string;
    address1: string;
    city: string;
    stateProvince: string;
    country: string;
    companyName: string;
    eventName: string;
    expectedGuestCount: number;
    eventDate: Date;
    eventStart: Date;
    eventEnd: Date;
    laterSchedDate: Date;
    eventNotes: string;
    category: any;
    theme: any;
    distanceInMiles: number;
    travelTimeInMins: number;
    county: string;
    orderPickListOptions: any[];
    eventFieldStartTime: Date;
    eventFieldEndTime: Date;
    requestedPickupAndDeliveryTime: Date;
    calculatedEstimatedTimeFromAPI: Date;
    appUserState: any;
    discountTotal: number;
    discount: string;
    openHoursList: any;
  };

  // Props/dependencies
  organizationSettings: any;
  organizations: any;
  order: any;
  appUserDetails: any;
  organizationAddress: any;

  // Callback functions
  validateGuestCount: () => boolean;
  validateMenuCategoryItems: (payload: any) => Promise<any>;
  createOrderGuest: (payload: any) => Promise<any>;
  createNewAddress: (payload: any) => Promise<any>;
  clearCart: () => void;
  orderRemoveItem: (itemId: number) => Promise<any>;
  getCalculatedEstimatedTimeFromAPI: any;
  fetchCalculatedEstimatedTime: (payload: any) => Promise<any>; 
  getOrganizationOrdersOpenSchedule: (orderType: string) => Promise<any>;
  checkAppUserAvailable: () => Promise<void>;
  getOrderDeliveryAddress: () => any;

  // State setters
  setState: (updates: any) => void;

  // Router
  router: any;
}

export const handlePayLater = async (params: PayLaterHandlerParams) => {
  const {
    state,
    organizationSettings,
    organizations,
    order,
    appUserDetails,
    validateGuestCount,
    validateMenuCategoryItems,
    createOrderGuest,
    createNewAddress,
    clearCart,
    orderRemoveItem,
    getCalculatedEstimatedTimeFromAPI,
    fetchCalculatedEstimatedTime,
    getOrganizationOrdersOpenSchedule,
    checkAppUserAvailable,
    getOrderDeliveryAddress,
    setState,
    organizationAddress,
    router
  } = params;

  // Validate guest count
  if (!validateGuestCount()) {
    return;
  }

  const {
    firstName,
    lastName,
    phoneNumber,
    email,
    notes,
    orderType,
    undeliverablePostalCodes,
    undeliverableDistances,
    undeliverableTravelTime,
    postalCode,
    selectedAddressId,
    savedAddresses,
    saveAddress,
    addressName,
    address1,
    city,
    stateProvince,
    country,
    companyName,
    eventName,
    expectedGuestCount,
    eventDate,
    laterSchedDate,
    eventNotes,
    category,
    theme,
    distanceInMiles,
    travelTimeInMins,
    county,
    orderPickListOptions,
    eventFieldStartTime,
    eventFieldEndTime,
    requestedPickupAndDeliveryTime,
    calculatedEstimatedTimeFromAPI,
    appUserState,
    discountTotal,
    openHoursList,
    discount
  } = state;

  const start = moment(eventDate).format('YYYY-MM-DD') + 
                moment(eventFieldStartTime).utc().format('THH:mm:ss.SSS[Z]');

  const selectedAddress = savedAddresses?.find((address: any) => address?.id === selectedAddressId);
  const invalidPostalCode = selectedAddress 
    ? undeliverablePostalCodes?.find((code: string) => code === selectedAddress?.postalCode)
    : undeliverablePostalCodes?.find((code: string) => code === postalCode);
  const invalidDistanceInMiles = undeliverableDistances?.find((dist: string) => distanceInMiles >= Number(dist));
  const invalidDriveDuration = undeliverableTravelTime?.find((time: string) => travelTimeInMins >= Number(time));

  if (orderType === "Delivery" && (invalidPostalCode || invalidDistanceInMiles || invalidDriveDuration)) {
    setState({ 
      orderCreateError: true, 
      orderCreateErrorMessage: organizationSettings?.ordersUndeliverableMessage 
    });
    return;
  }

  const orderDateTime = orderType === "Event" 
    ? moment(start)
    : moment(laterSchedDate).set({
        hour: moment(requestedPickupAndDeliveryTime).hour(),
        minute: moment(requestedPickupAndDeliveryTime).minute(),
        second: moment(requestedPickupAndDeliveryTime).second()
      });

  if (orderDateTime.isBefore(moment())) {
    setState({ 
      orderCreateError: true, 
      orderCreateErrorMessage: "Please select a Date and Time in the future"
    });
    return;
  }

  const openHoursResponse = await getOrganizationOrdersOpenSchedule(orderType);
  const scheduleType = openHoursResponse?.data ? orderType : "Base";

  const responseDate = await fetchCalculatedEstimatedTime({ orderItems: order.orderItems, scheduleType }); 

  const responseMoment = moment.utc(responseDate.data).local();
  const orderMomentUTC = moment(orderDateTime);

  if (orderMomentUTC.isBefore(responseMoment)) {
    const roundedTime = roundToNext30Min(responseMoment.toDate());
    const roundedDate = roundedTime instanceof Date ? roundedTime : (roundedTime as any).toDate();
    const isValidDate = roundedDate instanceof Date && !isNaN(roundedDate.getTime());

    const originalDate = moment(laterSchedDate).format("YYYY-MM-DD");
    const newDate = moment(roundedDate).format("YYYY-MM-DD");
    const dateChanged = originalDate !== newDate;

    setState({ 
      laterSchedDate: isValidDate ? new Date(roundedDate) : new Date(),
      requestedPickupAndDeliveryTime: isValidDate ? new Date(roundedDate) : new Date(),
      openHoursList: {
        ...openHoursList,
        openFrom: new Date(roundedDate)
      },
      eventDate: isValidDate ? new Date(roundedDate) : new Date(),
      eventFieldStartTime: isValidDate ? new Date(roundedDate) : new Date(),
      dateUpdatedMessage: dateChanged ? `Date was updated, please review` : "",
      timeUpdatedMessage: `Time was updated, please review`,
      eventDateUpdatedMessage: dateChanged ? `Date was updated, please review` : "" ,
      eventTimeUpdatedMessage: `Time was updated, please review`,
      orderCreateError: true, 
      orderCreateErrorMessage: `The earliest available date and time is ${moment(roundedDate).format("YYYY/MM/DD hh:mm A")}`,
    });
    return;
  }
  
  const menuCategoryItems = order.orderItems.map((item: any) => item?.menuCategoryItem?.id);
  const response = await validateMenuCategoryItems({ 
    "organizationId": organizations?.organization?.id, 
    "menuCategoryItems": menuCategoryItems
  });

  if (response?.data?.menuCategoryItems && response?.data?.menuCategoryItems.length > 0) {
    const invalidItemsIds = response?.data?.menuCategoryItems;
    
    for (const invalidItemId of invalidItemsIds) {
      const invalidItem = order.orderItems.find(
        (item: any) => item?.menuCategoryItem?.id === invalidItemId
      );
      
      if (invalidItem) {
        const itemName = invalidItem?.name || 
                        invalidItem.menuCategoryItem?.menuItem?.name || 
                        'Unknown Item';
        
        setState({ 
          orderCreateError: true, 
          orderCreateErrorMessage: `Item ${itemName} is no longer available`
        });
        
        await orderRemoveItem(invalidItem.id);
      }
    }
    
    getCalculatedEstimatedTimeFromAPI();
    return;
  }

  setState({ creatingOrder: true });
  await checkAppUserAvailable();

  const orderItems: any[] = [];
  const userDetails: any = appUserState ? appUserState : appUserDetails;

  order.orderItems.forEach((orderItem: IOrderItem) => {
    delete orderItem.menuCategoryItem;
    orderItem.organization = { id: organizations?.organization?.id };
    orderItems.push({
      notes: orderItem?.notes,
      orderItemModifiers: transformArrayFromModifiers(orderItem?.orderItemModifiers),
      menuCategoryItemId: orderItem?.menuCategoryItemId,
      quantity: orderItem?.quantity
    });
  });

  const startTime = moment(eventDate)
    .hour(moment(eventFieldStartTime).hour())
    .minute(moment(eventFieldStartTime).minute())
    .second(0)
    .millisecond(0)
    .utc()
    .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');

  const endTime = moment(eventDate)
    .hour(moment(eventFieldEndTime).hour())
    .minute(moment(eventFieldEndTime).minute())
    .second(0)
    .millisecond(0)
    .utc()
    .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');

  const newRequestedDateTime = moment(laterSchedDate)
    .hour(moment(requestedPickupAndDeliveryTime).hour())
    .minute(moment(requestedPickupAndDeliveryTime).minute())
    .second(0)
    .millisecond(0)
    .utc()
    .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');

  const payload = [{
    organizationId: organizations?.organization?.id,
    "orderItems": orderItems,
    "orderRevenueTypeRates": [],
    orderType,
    "orderStatusType": "New",
    customerFirstName: firstName,
    customerLastName: lastName,
    customerPhone: phoneNumber,
    customerEmail: email,
    notes: orderType === "Event" ? eventNotes : notes,
    "isAsap": false,
    "requestedDateTime": orderType === 'Event' ? startTime : newRequestedDateTime,
    "orderDeliveryAddress": orderType === 'Pickup' ? organizationAddress : getOrderDeliveryAddress(),
    "expectedGuestCount": orderType === 'Event' ? expectedGuestCount : (organizationSettings?.enableGuestCount ? expectedGuestCount : 0),
    userId: userDetails?.id,
    "orderPickListOptions": orderPickListOptions || [],
    "orderEventDetails": orderType === "Event" ? {
      "categoryId": category || null,
      "themeId": theme || null,
      "company": companyName,
      "eventName": eventName,
      "eventStartTime": startTime,
      "eventEndTime": endTime
    } : null,
    user: {
      organizationId: organizations?.organization?.id,
      id: userDetails?.id,
    },
    ...(discountTotal > 0 && { discountCode: discount })
  }];

  const addressPayload = [{
    name: addressName,
    address1: replaceComma(address1),
    city,
    stateProvince,
    county,
    postalCode,
    country: country || "United States",
    isDefault: savedAddresses?.length === 0 ? true : false
  }];  

  try {
    const res = await createOrderGuest(payload);
    setState({ creatingOrder: false });

    if (res?.status === 200) {
      if (saveAddress) {
        await createNewAddress(addressPayload);
      }
      clearCart();
      router?.navigate(`/order-confirmed/${res?.data[0]?.id}`);
      localStorage.removeItem("order");
      localStorage.removeItem("requestedPickupDateTime");
      localStorage.removeItem("eventEndDateTime");
    } else {
      const messageParts = res?.message.split(".\n");
      const errorMessage = messageParts.length > 1 ? messageParts[1] : res?.message;
      setState({ 
        orderCreateError: true, 
        orderCreateErrorMessage: errorMessage === "You can't create an order in the past" 
          ? "Please select a Date and Time in the future" 
          : errorMessage 
      });
    }
  } catch (err: any) {
    setState({ creatingOrder: false });
  }
};